import { v4 as uuid } from "uuid";

import firebase from "js/firebase";
import { isOfflinePlayer, isCypress, isKarma, clientName, buildVersion } from "js/config";

import { LogGroup } from "js/core/logger/enums";
import Logger from "js/core/logger/Logger";
import ConsoleTransport from "js/core/logger/transports/ConsoleTransport";
import ApiTransport from "js/core/logger/transports/ApiTransport";

// Context for transports (is static for the whole session)
interface LoggerTransportContext {
    browserId: string
    sessionId: string
    clientName: string
    clientVersion: string
    userAgent: string
}

// Context for log entries (is dynamic for each log entry)
interface LogEntryContext {
    uid: string | undefined,
    location: string
}

// Id of the current session (gets reset on page refresh and bundle reload)
// used as trace id
const sessionId = uuid();

// Logging client id, basically browser id
let browserId = localStorage.getItem("bai-browser-id");
if (!browserId) {
    browserId = uuid();
    localStorage.setItem("bai-browser-id", browserId);
}

// Transports we use
const transportContext: LoggerTransportContext = {
    sessionId,
    browserId,
    clientName,
    clientVersion: buildVersion,
    userAgent: navigator.userAgent
};

const transports = [new ConsoleTransport<LoggerTransportContext, LogEntryContext>(transportContext)];
if (!isOfflinePlayer && !isCypress && !isKarma) {
    transports.push(new ApiTransport<LoggerTransportContext, LogEntryContext>(transportContext));
}

// Custom entries context generator
function getLogEntryContext(): LogEntryContext {
    return {
        uid: isOfflinePlayer ? undefined : firebase.auth().currentUser?.uid,
        location: window.location.origin + window.location.pathname,
    };
}

// Loggers, will be lazy initialized (except for window logger)
const loggers: Partial<Record<LogGroup, Logger<LoggerTransportContext, LogEntryContext>>> = {
    [LogGroup.WINDOW]: new Logger<LoggerTransportContext, LogEntryContext>(transports, getLogEntryContext, LogGroup.WINDOW)
};

/**
 * Returns a logger for the specified logging group
 */
function getLogger(group: LogGroup = LogGroup.DEFAULT) {
    if (!loggers[group]) {
        loggers[group] = new Logger<LoggerTransportContext, LogEntryContext>(transports, getLogEntryContext, group);
    }

    return loggers[group];
}

export default getLogger;

export {
    sessionId,
    browserId,
    getLogger,
    LogGroup
};
